import { Outlet, useLocation, useOutletContext } from "react-router";
import CookieConsent from "~/components/CookieConsent";
import CreateAccountWizard from "~/components/CreateAccountWizard";
import Flash from "~/components/Flash";
import FooterMaximus from "~/components/FooterMaximus";
import FooterMinimus from "~/components/FooterMinimus";
import GtmIframe from "~/components/GtmIframe";
import HeaderMaximus from "~/components/HeaderMaximus";
import HeaderMinimus from "~/components/HeaderMinimus";
import MarketingMessages from "~/components/MarketingMessages";
import Scripts from "~/components/Scripts";
import { RootLoaderContext, StoreLoaderContext } from "~/entities/global/types";
import useStoreHydration from "~/hooks/useStoreHydration";
import loader from "~/routes/pages/_store/loader";
import shouldRevalidate from "~/routes/pages/_store/shouldRevalidate";
import type { Route } from "./+types";

export { loader, shouldRevalidate };

export const useUser = () => {
  const { user } = useOutletContext<StoreLoaderContext>();
  return user;
};

export const useCart = () => {
  const { cart } = useOutletContext<StoreLoaderContext>();
  return cart;
};

const Store = ({ loaderData }: Route.ComponentProps) => {
  const {
    flash: cookieFlash,
    cart,
    isSignedIn,
    mills,
    user,
    nonce,
    isNewCheckoutEnabled,
    cartGlobalHeaderEnabled,
  } = loaderData;
  const context = useOutletContext<RootLoaderContext>();
  const { head } = context;
  const { flash, gtmID } = useStoreHydration({
    cookieFlash,
    user,
    head,
    nonce,
  });
  const { pathname } = useLocation();
  const isHeaderMaximusLayout = pathname !== "/cart" || cartGlobalHeaderEnabled;

  return (
    <>
      <GtmIframe
        user={user}
        gtmID={gtmID}
      />
      <a
        className="absolute h-0 opacity-0"
        href="#content"
      >
        Skip to content
      </a>
      {isHeaderMaximusLayout ? (
        <HeaderMaximus
          cart={cart}
          hasSession={isSignedIn}
          mills={mills}
          isNewWizard={isNewCheckoutEnabled}
        />
      ) : (
        <HeaderMinimus
          hasSession={isSignedIn}
          isNewWizard={isNewCheckoutEnabled}
        />
      )}
      <main
        id="content"
        className={`relative py-[6.25rem] print:py-0 md:pb-[2.5rem] ${isHeaderMaximusLayout ? "md:pt-[7.625rem]" : "md:pt-0"}`}
      >
        {isHeaderMaximusLayout && <MarketingMessages />}
        <Flash flashes={flash} />
        <Outlet
          context={
            {
              ...context,
              isGTMReady: !!gtmID,
              user,
              cart,
            } satisfies StoreLoaderContext
          }
        />
      </main>
      <CreateAccountWizard />
      {isHeaderMaximusLayout ? <FooterMaximus /> : <FooterMinimus />}
      <CookieConsent />
      <Scripts
        nonce={nonce}
        user={user}
      />
    </>
  );
};

export default Store;
